import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { FaAnglesRight, FaCircle } from "react-icons/fa6";
import "./Portfolio.css";
import PortfolioItem from "./PortfolioItem";
import PortfolioModal from "./components/PortfolioModal";
import { formatExperience, getExperienceDuration } from "./experience";

export default function Portfolio() {
  const data = useMemo(
    () => [
      {
        id: 1,
        title: "Business Analyst / Programmer & PM Internship",
        date: "May 2017 – Dec 2017",
        company_name: "BMW",
        description:
          "Built Oracle reports and live SQL executive dashboards integrated into SAP Business Objects. Analyzed production risk patterns for the BMW X7, partnered with plant leaders to streamline workflows, and supported project coordination to reduce downtime and improve operational visibility.",
        tech_stack: ["SQL", "Oracle", "SAP BO", "Excel"],
        imageUrl: "images/bmw.png",
        motionUrl: "images/bmw.gif",
      },
      {
        id: 2,
        title: "Freelance Full-Stack Developer",
        date: "Jul 2017 – Feb 2018",
        company_name: "Upwork",
        description:
          "Delivered an end-to-end Ruby on Rails website for a blogger with image uploads and user collaboration through comments. Implemented MVC architecture, used PostgreSQL for persistence, and stored assets in AWS S3 for reliable, scalable storage.",
        tech_stack: ["Ruby on Rails", "PostgreSQL", "AWS"],
        imageUrl: "images/upwork.png",
        motionUrl: "images/ruby.gif",
      },
      {
        id: 3,
        title: "Senior Application Developer",
        date: "Jan 2018 – Nov 2021",
        company_name: "UPS",
        description:
          "Progressed from Software Engineer I/II to Senior Application Developer, delivering production-grade shipping and tracking platforms. Improved UI responsiveness (Angular 7), expanded C#/.NET backend capabilities, integrated major carrier APIs for real-time visibility, and resolved cross-stack defects to strengthen reliability and performance.",
        tech_stack: ["C#", "Angular 7", "ASP.NET MVC", "API"],
        imageUrl: "images/ups.png",
        motionUrl: "images/ups.webp",
      },
      {
        id: 4,
        title: "Software Engineer III — Analytics",
        date: "Nov 2021 – Sep 2024",
        company_name: "Chick-fil-A",
        description:
          "Promoted into full ownership of analytics engineering delivery and architecture. Designed Airflow + Python ETL pipelines into AWS Redshift, led Oracle → Redshift migration to reduce cost and improve performance, and launched Data Quality & QA Automation with test frameworks plus a Flask-based QA platform to cut manual validation.",
        tech_stack: ["Python", "AWS", "Airflow", "Redshift", "Terraform", "Snowflake"],
        imageUrl: "images/cfa.png",
        motionUrl: "images/cfa.gif",
      },
      {
        id: 5,
        title: "Senior Data Engineer",
        date: "Sep 2024 – Aug 2025",
        company_name: "CoStar",
        description:
          "Led architecture for a serverless, event-driven ETL platform using AWS Step Functions, Lambda, and CloudWatch for Homes.com/Apartments.com leasing data. Built S3 + Databricks ingestion foundations adopted enterprise-wide, drove $300K+ annual savings replacing RDS-based CDC, and owned Terraform automation plus monitoring and S3 Glacier lifecycle management.",
        tech_stack: ["Python", "AWS", "Databricks", "Terraform", "SQL"],
        imageUrl: "images/costar.png",
        motionUrl: "images/costar.gif",
      },
      {
        id: 6,
        title: "Senior Data Engineer",
        date: "Aug 2025 – Present",
        company_name: "Cargill",
        description:
          "Lead the Atlanta data engineering function and mentor a junior engineer while expanding the regional footprint. Serve on the Cargill Cares Council board, building partnerships with Georgia nonprofits and supporting employee-led volunteer, fundraising, and community outreach initiatives. Own S3 to Hadoop HDFS migration pipelines through Hue and provide governed SQL access through Impala external tables for global trading operations.",
        tech_stack: ["Python", "AWS", "Hadoop", "Impala", "Cloudera"],
        imageUrl: "images/Cargill.png",
        motionUrl: "images/cargill.gif",
      },
    ],
    []
  );

  const filters = useMemo(() => ["All", "Python", "C#", "AWS", "Hadoop", "Airflow"], []);
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [journeyFocus, setJourneyFocus] = useState(null);

  const cardRefs = useRef({});

  const filtered = useMemo(() => {
    if (activeFilter === "All") return data;
    return data.filter((x) => (x.tech_stack || []).includes(activeFilter));
  }, [activeFilter, data]);

  const totalExperience = useMemo(
    () => `${formatExperience(getExperienceDuration(), true)}+`,
    []
  );

  const openModal = useCallback((item) => {
    if (!item) return;
    setSelectedItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedItem(null);
  }, []);

  const scrollToCard = useCallback((id) => {
    const el = cardRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const onJourneyRoleClick = useCallback(
    (id) => {
      setJourneyFocus(null);
      setHighlightId(id);
      scrollToCard(id);

      const item = data.find((d) => d.id === id);
      if (item) setTimeout(() => openModal(item), 200);

      setTimeout(() => setHighlightId(null), 2000);
    },
    [data, openModal, scrollToCard]
  );

  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [modalOpen]);

  const journeyNodes = useMemo(
    () => [
      { type: "role", id: 1, year: "2017", label: "BMW", logo: "images/bmw.png" },
      {
        type: "edu",
        id: "bsc",
        year: "2017",
        label: "GGC",
        logo: "images/georgiagwinnett.png",
        focus: {
          title: "B.Sc. — Georgia Gwinnett College",
          detail: "Software Development (Cum Laude)",
          year: "2017",
        },
      },
      { type: "role", id: 2, year: "2017", label: "Upwork", logo: "images/upwork.png" },
      { type: "role", id: 3, year: "2018", label: "UPS", logo: "images/ups.png", senior: true },
      {
        type: "edu",
        id: "msc",
        year: "2021",
        label: "GSU",
        logo: "images/georgiastate.png",
        focus: {
          title: "M.Sc. — Georgia State University",
          detail: "Information Systems (Big Data & Analytics)",
          year: "2021",
        },
      },
      { type: "role", id: 4, year: "2021", label: "Chick-fil-A", logo: "images/cfa.png" },
      { type: "role", id: 5, year: "2024", label: "CoStar", logo: "images/costar.png" },
      { type: "role", id: 6, year: "2025", label: "Cargill", logo: "images/Cargill.png", senior: true },
    ],
    []
  );

  const onJourneyClick = useCallback(
    (node) => {
      if (node.type === "role") {
        onJourneyRoleClick(node.id);
        return;
      }
      if (node.type === "edu") {
        setJourneyFocus(node.focus || null);
      }
    },
    [onJourneyRoleClick]
  );

  return (
    <>
      <Helmet>
        <title>Portfolio</title>
        <meta
          name="description"
          content="Khaled Asad's data engineering experience across AWS, analytics platforms, and enterprise software."
        />
      </Helmet>

      <div className="portfolio-page">
        <div className="portfolio-wrap">
          <section className="journey">
            <div className="journey__header">
              <h1 className="journey__title">Journey</h1>
              <div className="journey__xp">{totalExperience}</div>
            </div>

            <div className="journey__scrollHint" aria-hidden="true">Swipe timeline →</div>

            <div className="journey__trackWrap" tabIndex="0" aria-label="Career timeline, scroll horizontally">
              <div className="journey__track" aria-hidden="true">
                <span className="journey__directionLight"><FaCircle /></span>
                <span className="journey__directionArrow"><FaAnglesRight /></span>
              </div>
              <div className="journey__nodes">
                {journeyNodes.map((n, idx) => {
                  const isEdu = n.type === "edu";
                  return (
                    <button
                      key={`${n.type}-${n.id}-${idx}`}
                      type="button"
                      className={[
                        "journey__node",
                        n.senior ? "is-senior" : "",
                        isEdu ? "is-edu" : "",
                      ].join(" ")}
                      onClick={() => onJourneyClick(n)}
                      title={`${n.label} ${n.year}`}
                      aria-label={`${n.label} ${n.year}`}
                    >
                      <span className="journey__bubble">
                        <img
                          src={`${process.env.PUBLIC_URL}/${n.logo}`}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </span>

                      <span className="journey__year">{n.year}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {journeyFocus && (
              <div className="journey__eduCard">
                <div className="journey__eduTitle">{journeyFocus.title}</div>
                <div className="journey__eduDetail">{journeyFocus.detail}</div>
                <div className="journey__eduYear">{journeyFocus.year}</div>
              </div>
            )}
          </section>

          <div className="portfolio-filters" role="group" aria-label="Filter experience by technology">
            {filters.map((f) => (
              <button
                key={f}
                type="button"
                className={["filterChip", activeFilter === f ? "is-active" : ""].join(" ")}
                onClick={() => setActiveFilter(f)}
                aria-pressed={activeFilter === f}
              >
                {f}
              </button>
            ))}
          </div>

          <section className="portfolio-grid" aria-labelledby="experience-heading">
            <h2 id="experience-heading" className="srOnly">Professional experience</h2>
            {filtered.map((item) => (
              <PortfolioItem
                key={item.id}
                ref={(el) => {
                  if (el) cardRefs.current[item.id] = el;
                }}
                item={item}
                highlighted={highlightId === item.id}
                onOpen={() => openModal(item)}
              />
            ))}
          </section>
        </div>

        <PortfolioModal isOpen={modalOpen} onRequestClose={closeModal} content={selectedItem || {}} />
      </div>
    </>
  );
}
